import { useMemo } from 'react'
import { useCloudContext } from '../contexts/CloudProvider.jsx'
import { useDeviceStatus } from '../hooks/useDeviceStatus.js'
import { useDeviceHealth } from '../hooks/useDeviceHealth.js'
import { 
  HEALTH_STATUS_CONFIGS,
  useDeviceStatusCalculation,
} from '../hooks/useDeviceStatusCalculation.js'

/**
 * Device Status Indicators component
 * Shows real-time device status using Firestore and fault data
 * - Connection: Online/offline based on 60-second threshold
 * - Health: Based on current device faults
 * - Faults: Active fault count display
 */
function DeviceStatusIndicators() {
  const { selectedDeviceId } = useCloudContext()
  const { status, loading } = useDeviceStatus(selectedDeviceId)
  const { healthStatus, healthDetails } = useDeviceHealth(selectedDeviceId)
  
  // Use shared device status calculation hook
  const { connectionStatus, connectionConfig, lastSeenText } = useDeviceStatusCalculation(status)

  // override health status when offline
  const healthConfig = useMemo(() => {
    if (connectionStatus !== 'online') {
      return HEALTH_STATUS_CONFIGS.unknown
    }
    return HEALTH_STATUS_CONFIGS[healthStatus] || HEALTH_STATUS_CONFIGS.unknown
  }, [healthStatus, connectionStatus])
  
  const connectionTooltip = useMemo(() => 
    `Connection: ${connectionConfig.text}${lastSeenText ? ` (Last seen: ${lastSeenText})` : ''}`,
    [connectionConfig.text, lastSeenText],
  )
  
  const healthTooltip = useMemo(() => {
    const baseText = `Health: ${healthConfig.text}`
    const faultText = healthDetails.faultCount > 0 
      ? ` - ${healthDetails.faultCount} active fault${healthDetails.faultCount > 1 ? 's' : ''}`
      : ''
    const criticalText = healthDetails.issues?.length 
      ? ` - Critical: ${healthDetails.issues.join(', ')}`
      : ''
    const warningText = healthDetails.warnings?.length 
      ? ` - Warnings: ${healthDetails.warnings.join(', ')}`
      : ''
    
    return `${baseText}${faultText}${criticalText}${warningText}`
  }, [healthConfig.text, healthDetails])
  
  if (!selectedDeviceId || loading) return null
  
  
  return (
    <>
      {/* Connection Status Indicator */}
      <div 
        className="status-item-enhanced online"
        title={connectionTooltip}
      >
        <span className={`status-dot-enhanced ${connectionStatus}`}></span>
        <span>{connectionConfig.text}</span>
      </div>
      
      {/* Health Status Indicator */}
      <div 
        className="status-item-enhanced"
        title={healthTooltip}
      >
        <i className={`${healthConfig.icon} ${healthConfig.color}`}></i>
        <span>{healthConfig.text}</span>
      </div>
      
      {/* Active Faults Count */}
      {healthDetails.faultCount > 0 && (
        <div className="status-item-enhanced warning">
          <i className="bi bi-exclamation-triangle"></i>
          <span>
            {healthDetails.faultCount} Fault{healthDetails.faultCount > 1 ? 's' : ''}
          </span>
        </div>
      )}
    </>
  )
}

export default DeviceStatusIndicators
